/* eslint-disable max-lines */
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import H2 from '@base/H2/H2';
import P1 from '@base/P1/P1';
import H3 from '@base/H3/H3';
import ContentsTable from '@base/ContentsTable/ContentsTable';
import MenusNav from '@base/MenusNav/MenusNav';
import { menus } from '@utils/constants';
import { Spacings } from '@styles/index';
import Layout from '@layout/index';
import LeftContainer from '@cont/CompanyDetails/LeftContainer';
import RightContainer from '@cont/CompanyDetails/RightContainer';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import Link from 'next/link';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: span 24;
`;

const StyledH3 = styled(H3)`
  margin-bottom: ${Spacings.SPACING_6B};
  padding-top: 100px;
  margin-top: -100px;
`;

const Paragraph = styled(P1)`
  margin-bottom: ${Spacings.SPACING_4B};
  text-align: justify;
`;

const StyledParagraph = styled(Paragraph)`
  margin-bottom: ${Spacings.SPACING_4B};
  ol > li {
    margin-bottom: ${Spacings.SPACING_2B};
    &:first-of-type {
      margin-top: ${Spacings.SPACING_2B};
    }
  }
`;

const ListItem = ({ children }) => (
  <StyledParagraph as="li">{children}</StyledParagraph>
);

const tableData = [
  {
    label: 'Eligibility and Entry to the Event',
    id: 'eligibility_and_entry_to_the_event'
  },
  { label: 'Age Requirements', id: 'age_requirements' },
  {
    label: 'Personal Information',
    id: 'personal_information'
  },
  {
    label: 'Consent And Waiver By Attendees',
    id: 'consent_and_waiver_by_attendees'
  },
  { label: 'Prohibited Items', id: 'prohibited_items' },
  { label: 'General Conditions', id: 'general_conditions' },
  {
    label: 'Additional Conditions',
    id: 'additional_conditions'
  },
  {
    label: 'Disclaimers',
    id: 'disclaimers'
  }
];

const RankPredictorTerms = () => {
  const router = useRouter();
  const { pathname } = router;

  return (
    <>
      <LeftContainer>
        <MenusNav data={menus} currentHref={pathname} />
      </LeftContainer>

      <RightContainer>
        <Wrapper>
          <div>
            <div>
              <H2 as="h1">Unacademy One Kota Terms and Conditions</H2>
            </div>
            <br />
            <br />
            <ContentsTable data={tableData} />
            <Paragraph>
              The Unacademy One Kota (“<b>Event</b>”) is an ‘<i>invite only</i>’
              private event organized by M/s Sorting Hat Technologies Private
              Limited (“<b>Unacademy</b>”) at Kota, Rajasthan. The Event shall
              be governed by the following terms and conditions as well as the
              <Link href="/terms"> Terms and Conditions </Link>and
              <Link href="/privacy"> Privacy Policy </Link>
              as available on the Unacademy Platform (including website and
              mobile applications) (collectively referred to as “<b>Terms</b>”)
              which shall be binding on the Attendees (defined later) any other
              individuals attending the event.
            </Paragraph>
            <Paragraph>
              The Event is tentatively scheduled to be conducted on Kota on
              Sunday, October 16, 2022 at Chapan Bhog, 5R8H+X2H, RJ SH 33,
              Kishorepura (“<b>Venue</b>”). Further details related to the event
              shall be communicated to Attendees through email, SMS or via any
              other mode by Unacademy.
            </Paragraph>
            <br />
            <br />
            <a name={tableData[0].id}>
              <StyledH3>{tableData[0].label}</StyledH3>
            </a>
            <ol>
              <ListItem>
                Unacademy’s learners (“<b>Learner(s)</b>”) from Kota who have
                received a communication in the form of SMS/email from Unacademy
                containing a unique link must register for the Event to be
                eligible to get a chance to get invited for the Event. Upon
                successful registration within the time stipulated in such
                SMS/email, Learners may get a chance to receive a confirmation
                email from Unacademy with details of the Event (“
                <b>Participant Learner(s)</b>”) confirming their invitation and
                participation to the Event.{' '}
              </ListItem>
              <ListItem>
                Learners may invite up to two guests to attend the Event with
                him/her by sharing the communication received from Unacademy
                with the guests, provided that such guest is at least twelve
                years of age and subject to the terms and conditions herein.
              </ListItem>
              <ListItem>
                In order to be selected for the Event, a guest shall be (i) an
                Individual (ii) an Indian citizen (iii) in India (iv) legally
                eligible to enter into a contract or fulfills the Age
                Requirements provided below, provided that the guest must have
                (i) successfully registered for the Event (ii) Learner from whom
                they have received the invite(s) have already registered for and
                are attending the Event (vi) complies with all other terms and
                conditions stated herein.
              </ListItem>
              <ListItem>
                Learners’ guests who are interested in attending the event must
                register for the Event through the unique link shared by the
                Learner (“<b>Registered Guest(s)</b>”). Please note that solely
                by virtue of registering on the link shared by the Learner
                alone, a guest shall not be eligible to attend the Event.
                Unacademy at its sole discretion will issue confirmation emails
                to the selected guests (“<b>Invitees</b>”), where Learner will
                get priority over the Learners’ guests, and that Learners’
                guests, subject to satisfaction of all terms and condition
                states herein will be eligible to get a chance to attend the
                Event on “first come first serve” basis’. Only successful
                registrations amongst Learner and Learners’ guests as per the
                eligibility and other terms and conditions mentioned herein will
                receive a confirmation email from Unacademy with details of the
                Event confirming their invitation and participation to the Event
                (“<b>Confirmation Email</b>”).
              </ListItem>
              <ListItem>
                In order to gain entry to the Event, the Participant Learner and
                the Invitee (collectively “<b>Attendee(s)</b>”) are required to
                show an Unacademy Student ID or any government ID card along
                with the Confirmation Email received by them which contain a
                unique QR code at the registration counters at the Venue of the
                Event. Attendees may be refused entry at the venue of the Event
                if the name on the Confirmation Email does not match with their
                individual IDs.
              </ListItem>
            </ol>
            <br />
            <br />
            <a name={tableData[1].id}>
              <StyledH3>{tableData[1].label}</StyledH3>
            </a>
            <ol>
              <ListItem>
                The Attendees understand and confirm that where they are less
                than eighteen years of age (“<b>Minor</b>”), the registration
                for the Event was done with the consent of and under the
                supervision of their parents or guardian.
              </ListItem>
              <ListItem>
                Attendees acknowledge that Unacademy does not have the
                responsibility to ensure that the Attendee is registering and
                attending the Event with the consent and supervision of their
                parent or guardian. It shall be the Attendee’s sole
                responsibility to ensure that the required qualifications are
                met.
              </ListItem>
              <ListItem>
                Unacademy reserves the right to revoke the Attendees’
                Confirmation Email to attend the Event/ refuse entry to the
                Event if it learns that the Attendees do not meet the above
                stipulations with respect to Minors.{' '}
              </ListItem>
            </ol>
            <br />
            <br />
            <a name={tableData[2].id}>
              <StyledH3>{tableData[2].label}</StyledH3>
            </a>
            <ol>
              <ListItem>
                A Participant Learners’ and the Registered Guests’ Personal
                Information, including Sensitive Personal Information, that may
                be collected to enable the Event and provide ancillary services
                related to the Event shall be collected, processed, and stored
                in accordance with the Unacademy’s Privacy Policy. Participant
                Learners or the Registered Guests shall have all the rights
                available to them under the applicable Data Protection Laws in
                accordance with the Unacademy Privacy Policy.
              </ListItem>
              <ListItem>
                Unacademy shall use a Participant Learners’ or the Registered
                Guests’ Personal Information for facilitating the Event and
                rendering the Services with respect to the Platform in the most
                effective manner, to secure the Platform and make improvements,
                to perform market research and product analysis, to carry out
                Unacademy’s obligations towards the Participant Learner or the
                Registered Guest, and to communicate with the Participant
                Learners and the Registered Guests. The said communication can
                either be by calls, text, or emails and for the purposes of
                conducting the Event itself, transactional, service, advertising
                or promotional calls or messages about the Event and other
                offers, new products or services made available by Unacademy and
                its affiliates, group and/or subsidiary companies, and the
                Participant Learner and Registered Guest hereby consents to the
                same. Any deletion request received from a Participant Learner
                and a Registered Guest in respect of their Personal Information
                shall be processed in accordance with Unacademy Privacy Policy.{' '}
              </ListItem>
            </ol>
            <br />
            <br />
            <a name={tableData[3].id}>
              <StyledH3>{tableData[3].label}</StyledH3>
            </a>
            <ol>
              <ListItem>
                The Attendees agree and consent to Unacademy and its affiliates,
                Group Companies, subsidiaries recording the Attendees’
                participation, inter alia, the Attendees’ interactions,
                appearance, name, qualification, testimony, photographs, audio
                and/or video of their participation, as may be applicable, (“
                <b>Recordings</b>”) for and / or in relation to the Event and to
                use the Recordings for the purpose of live-streaming, upload,
                publications, advertising, promotion of Unacademy or its group
                Companies or their online platforms on their online platforms,
                including but not limited to www.unacademy.com, YouTube and
                other social media channels, and other media activities
                (“Activities”).
                <br />
                <br />
                Further Attendee consents to the following:
                <ol type="a">
                  <ListItem>
                    The Attendee permits Unacademy to record the Event with
                    his/her participation and perpetually use the Recordings of
                    the Event for the Activities mentioned hereinabove,
                    including livestream on multiple platforms.
                  </ListItem>
                  <ListItem>
                    The Attendee agrees that the rights to control the use of
                    these Recordings throughout the world will perpetually
                    remain with Unacademy as its sole property.
                  </ListItem>
                  <ListItem>
                    The Attendee agrees and understands that he/she will not be
                    compensated in connection with use of these Recordings.
                  </ListItem>
                  <ListItem>
                    The Attendee confirms that any information including without
                    limitation testimonials, quotes etc. are true and accurate
                    to the best of his/her knowledge and hereby agree to
                    indemnify and hold Unacademy harmless against any claims
                    that may arise with relation to the same.
                  </ListItem>
                </ol>
              </ListItem>
            </ol>
            <br />
            <br />
            <a name={tableData[4].id}>
              <StyledH3>{tableData[4].label}</StyledH3>
            </a>
            <ol>
              <ListItem>
                The Attendees shall not carry any bags, water bottles or food
                items to the Venue. Such items shall be confiscated from the
                Attendees at the time of security checkpoint at the entry.
              </ListItem>
              <ListItem>
                Attendees are prohibited from bringing items such as Cigarettes,
                or any such smoking devices, alcohol, beverages to the Event and
                in case any such items are found with Attendees, such Attendees
                and their respective guest will not be permitted to enter to the
                Venue for attending the Event and will be asked to leave the
                Event Venue in such a case.
              </ListItem>
              <ListItem>
                Any Learner found in possession of any illegal or substances
                banned under the law, hazardous substances, or dangerous objects
                including but not limited to firearms, flammable items, aerosol,
                lighters, matches, tin cans et.c shall be handed over to the
                authorities or evicted from the Venue. Unacademy reserves the
                rights to seize, confiscate or hand over to the authorities any
                such items.
              </ListItem>
            </ol>
            <br />
            <br />
            <a name={tableData[5].id}>
              <StyledH3>{tableData[5].label}</StyledH3>
            </a>
            <ol>
              <ListItem>
                Information that is available on the Unacademy Platform or
                communicated on Unacademy’s official social media handles shall
                be the only authentic and valid information with respect to the
                Event. Unacademy shall not be responsible for any
                incorrect/misleading information relating to the Event,
                presented/ displayed on any other source other than
                the Unacademy Platform and shall not be liable for any loss
                suffered by any Learners or Learners’ guests based on the
                representation, terms, the information presented/displayed on
                any other source other than the Unacademy Platform.
              </ListItem>
              <ListItem>
                Unacademy reserves the right to refuse entrance and / or revoke
                access to any Attendee to the Event at any time and all
                decisions made by Unacademy in this regard are final.
              </ListItem>
              <ListItem>
                Unacademy reserves the right at its sole discretion to modify,
                cancel, postpone, reschedule or cancel the Event at any time
                without any prior written notice and Unacademy shall not be held
                liable for any losses, damages suffered by the Attendees as a
                result. In such instances, Unacademy shall endeavour to provide
                advance notice, where possible.
              </ListItem>
              <ListItem>
                The Attendees are advised to exercise necessary caution while
                attending the Event and Unacademy shall assume no liability in
                case an Attendee suffers any medical condition while attending
                the Event.
              </ListItem>
              <ListItem>
                These Terms shall be governed by the laws of India and subject
                to the Terms, the courts at Bangalore shall have exclusive
                jurisdiction to determine any disputes arising out of or in
                relation to the provision of these terms.
              </ListItem>
              <ListItem>
                The Attendees shall indemnify and hold harmless Unacademy, its
                officers, employees, agents, licensees, etc against any and all
                damages, claims, losses, demands, actions, causes of action,
                suits, litigation, costs and expenses (including but not limited
                to, the publication of response(s) submitted by the Attendee as
                a part of, or in association with the Event without limitation,
                reasonable attorneys’ and consultants’ fees).
              </ListItem>
              <ListItem>
                Unacademy shall not be liable for any failure to comply with its
                obligations caused by weather conditions, fire, flood, strike,
                hurricane, industrial dispute, war, hostilities, terrorist
                attack, political unrest, riots, civil commotion, inevitable
                accidents, pandemic and/or epidemic, acts of God or any other
                circumstances amounting to Force Majeure.
              </ListItem>
              <ListItem>
                Unacademy shall not be responsible for any loss or damage if
                Unacademy is required to discontinue or cancel the Event in
                compliance with any law, ruling, order, regulation, requirement,
                or instruction of any Central/State Government or for any other
                unavoidable reason beyond its control. The Attendees shall be
                informed of such discontinuance/cancellation as early as
                possible.
              </ListItem>
              <ListItem>
                Unacademy shall not be responsible for non-availability or
                deficiency in any services as may be rendered at the Venue by
                third parties.
              </ListItem>
              <ListItem>
                The aggregate liability of Unacademy in case of any damage,
                loss, cost, claim, liability, or expense (including legal costs
                and expenses) caused to the Attendee or incurred by any act,
                omission, or representation in respect of the Event shall not in
                any case exceed Rs.100.
              </ListItem>
            </ol>
            <a name={tableData[6].id}>
              <StyledH3>{tableData[6].label}</StyledH3>
            </a>
            <ol>
              <ListItem>
                Attendees are encouraged to arrive at least thirty minutes
                before the Event is slated to begin.
              </ListItem>
              <ListItem>
                Attendees who have been admitted to the Event must safeguard
                themselves at all times during the Event and always wear the
                wristband issued to them at the time of entry for the purpose of
                identification. Attendees who are not wearing a wristband or not
                adhering to instructions provided at the Venue may be restricted
                from participating in the Event and may even be asked to leave
                the Venue.
              </ListItem>
              <ListItem>
                Unacademy shall not be held responsible legally or otherwise in
                case:
                <ol type="a">
                  <ListItem>
                    Any personal item belonging to the Attendee goes missing/
                    stolen/lost/destroyed/confiscated anywhere at the Venue of
                    the Event.
                  </ListItem>
                  <ListItem>
                    Any personal injuries are sustained by the Attendees at the
                    Event arising from or related to any altercations between
                    Attendees.
                  </ListItem>
                </ol>
                Attendees acknowledges and agrees that Unacademy shall have no
                legal obligation to mitigate any Attendee’s actual or potential
                losses sustained during the Event.
              </ListItem>
              <ListItem>
                Attendees shall at all times follow the directions and obey the
                instructions of the Unacademy representatives at the Event Venue
                and shall not act in any manner that may amount to harassment,
                intimidation or causes general disruption or harm to the other
                Attendees or representatives of Unacademy at the Venue.
              </ListItem>
              <ListItem>
                Unacademy representatives shall have sole authority and shall
                have the sole discretion to prevent Invitees from entering the
                Venue for the Event. Such representatives shall have sole
                authority to compel an Attendee to leave the Venue and Event in
                case of any act or omission by the Attendee that can be deemed
                to be disruptive (at the sole discretion of such
                representative).
              </ListItem>
              <ListItem>
                Attendees understand that parking arrangements are not available
                at the Venue and those attending the Event are requested to
                arrange parking spaces for their vehicles.
              </ListItem>
              <ListItem>
                Confirmation Email sent to the Invitee(s) is a conditional,
                cancellable, personal, non-transferable, non-redeemable,
                revocable, and private invitation sent to the recipient of such
                Confirmation Email, being Invitees, solely for the purpose of
                single-entry usage to the Event Venue by respective Invitee.
                Invitees shall refrain themselves from sending or sharing such
                Confirmation Email to anyone else. Suitable actions will be
                taken against any direct or indirect misuse of such Confirmation
                Email and the recipient of such Confirmation Email shall be
                solely responsible for any such actions.{' '}
              </ListItem>
            </ol>

            <a name={tableData[7].id}>
              <StyledH3>{tableData[7].label}</StyledH3>
            </a>
            <ol>
              <ListItem>
                Unacademy and/or its subsidiaries/affiliates shall not be under
                any obligation to the Attendees in relation to the Event and the
                Attendees, Learners, Learners’ guests and/or Registered Guests
                shall have no claims whatsoever against Unacademy and/or its
                subsidiaries/affiliates relating to the selection process, or
                the management of the Event.
              </ListItem>
              <ListItem>
                Unacademy shall not be responsible for any errors or omissions
                in the terms and conditions contained herein. All information
                provided is provided “as is” without warranty of any kind. To
                the fullest extent permitted by applicable law, Unacademy
                disclaims all warranties, express or implied, including without
                limitation, implied warranties of merchantability, fitness for a
                particular purpose, and non-infringement in respect of the
                Event. Unacademy shall have the sole discretion to discontinue /
                cancel the Event or any part thereof at any point in time and
                the Attendees shall not raise any claim due to such
                discontinuation or cancellation at any time whatsoever.
              </ListItem>
              <ListItem>
                Unacademy shall not be held responsible in case of a server
                shutdown or loss of Learners’ or Registered Guests’ registration
                and/or other data. Unacademy shall not be responsible for any
                delay in receipt or non-receipt or incomplete or corrupt receipt
                of the registration or, other details of the Participant
                Learners and Registered Guests. Unacademy shall not be
                responsible for any technical difficulties or data corruption
                that may arise during the registration process. Further,
                Unacademy shall not be responsible and/or liable in any manner
                whatsoever in case any interested Learner or their guests are
                unable to register the Event due to failure on part of the
                internet service provider, facility provider, etc.
              </ListItem>
            </ol>
          </div>
        </Wrapper>
      </RightContainer>
    </>
  );
};

RankPredictorTerms.pageInfo = {
  name: 'Unacademy Rank Predictor Terms',
  isMWebSupported: true,
  layoutOptions: {
    hideGoalSelector: true,
    disableDarkMode: true
  }
};

export default RankPredictorTerms;
RankPredictorTerms.Layout = Layout;
RankPredictorTerms.Wrapper = PageReduxProvider;
